import { useMemo, Fragment, ReactNode } from 'react';

import { FormFieldType } from 'types';

import { FormInputField } from './input';
import { FormRadioField } from './radio';
import { FormSelectField } from './select';
import { OutcomesField } from './outcomes';
import { FieldArrayField } from './field-array';
import { DynamicFormFieldsProps } from './types';
import { FormScaleRatingField } from './scale-rating';
import { OutcomeFieldsField } from './outcome-fields';

export const DynamicFormFields = ({
  fields,
  container,
  hideLabels,
  expandableInput,
  fieldArrayProps,
  outcomesStateById,
  inputLabelOutside,
  isSelectSearchable,
  outcomeFieldsSourceStateById,
}: DynamicFormFieldsProps) => {
  const items = useMemo(() => {
    return fields.map((field, index) => {
      let fieldNode: ReactNode;
      const props = { ...field };
      if (hideLabels) {
        props.label = '';
      }
      switch (props.type) {
        case FormFieldType.INPUT:
          fieldNode = (
            <FormInputField
              key={props.id}
              expandable={expandableInput}
              labelOutside={inputLabelOutside}
              {...props}
            />
          );
          break;
        case FormFieldType.SELECT:
          fieldNode = (
            <FormSelectField
              key={props.id}
              labelOutside={inputLabelOutside}
              isSearchable={isSelectSearchable}
              {...props}
            />
          );
          break;
        case FormFieldType.RADIO:
          fieldNode = <FormRadioField key={props.id} {...props} />;
          break;
        case FormFieldType.SCALE_RATING:
          fieldNode = <FormScaleRatingField key={props.id} {...props} />;
          break;
        case FormFieldType.FIELD_ARRAY:
          fieldNode = (
            <FieldArrayField
              key={props.id}
              {...props}
              fieldArrayProps={fieldArrayProps}
            />
          );
          break;
        case FormFieldType.OUTCOMES:
          fieldNode = (
            <OutcomesField
              key={props.id}
              {...props}
              {...outcomesStateById?.[props.id]}
            />
          );
          break;
        case FormFieldType.OUTCOME_FIELDS:
          fieldNode = (
            <OutcomeFieldsField
              key={props.id}
              {...props}
              fieldArrayProps={fieldArrayProps}
              sourceValue={outcomeFieldsSourceStateById?.[props.id]?.value}
              sourceLoading={
                outcomeFieldsSourceStateById?.[props.id]?.isLoading
              }
            />
          );
          break;
        default:
          fieldNode = <Fragment key={index} />;
          break;
      }
      return container ? container(field, fieldNode, index) : fieldNode;
    });
  }, [
    fields,
    container,
    hideLabels,
    expandableInput,
    isSelectSearchable,
    inputLabelOutside,
    fieldArrayProps,
    outcomesStateById,
    outcomeFieldsSourceStateById,
  ]);

  return <>{items}</>;
};
